import { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import { ExtendedDataProvider } from './context/ExtendedDataContext';
import { AuthProvider } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { ProfilesProvider } from './context/ProfilesContext';
import { LoginPage } from './components/auth/LoginPage';
import { DownloadPage } from './components/DownloadPage';
import { AuthCallback } from './components/auth/AuthCallback';
import { OAuthAuthorize } from './components/auth/OAuthAuthorize';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { ToastContainer } from './components/shared/Toast';
import { ToastContainer as ErrorToastContainer } from './components/ToastContainer';
import { DialogProvider } from './components/shared/DialogProvider';
import { useTheme } from './hooks/useTheme';
import { useSpaces } from './hooks/useSpaces';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useMobile } from './hooks/useMobile';
import { useRouterSync } from './hooks/useRouterSync';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RemoteDatabaseBanner } from './components/RemoteDatabaseBanner';
import {
  ListViewRoute,
  BoardViewRoute,
  GanttViewRoute,
  InboxViewRoute,
  TeamViewRoute,
  TimesheetViewRoute,
  ProjectsViewRoute,
  DeviationsViewRoute,
  SprintViewRoute,
  CalendarViewRoute,
  TeamCalendarViewRoute,
  ReportViewRoute,
} from './routes/ViewRoutes';
import {
  DocumentRoute,
  ChatRoute,
  VoiceChannelRoute,
  SpreadsheetRoute,
  PresentationRoute,
} from './routes/ItemRoutes';
import { AlertCircle, Loader2, Download } from 'lucide-react';
import { parseUrl } from './utils/urlParsing';
import { TaskDetail } from './components/task/TaskDetail';
import { useAppStore } from './store/appStore';
import { useTauriUpdater } from './hooks/useTauriUpdater';
import { ProfilePage } from './components/profile/ProfilePage';
import { PublicProfilePage } from './components/profile/PublicProfilePage';

function GlobalTaskDetail() {
  const selectedTaskId = useAppStore((state) => state.selectedTaskId);
  const setSelectedTaskId = useAppStore((state) => state.setSelectedTaskId);
  const { tasks } = useDatabase();
  const { containersMap } = useSpaces();

  const selectedTask = useMemo(() => {
    if (!selectedTaskId) return null;
    const task = tasks.find(t => t.id === selectedTaskId) || null;
    if (!task || !task.spaceId || !containersMap) return task;
    // Enrich with spaceName, folderPath, listName for the Posizione section
    const container = containersMap.get(task.spaceId);
    if (!container) return task;
    const displayParts = ((container as { displayPath?: string }).displayPath || container.path).split('/');
    return {
      ...task,
      spaceName: displayParts[0],
      folderPath: displayParts.length > 1 ? displayParts.slice(1, -1) : [],
      listName: container.type === 'list' ? displayParts[displayParts.length - 1] : undefined,
    };
  }, [selectedTaskId, tasks, containersMap]);

  if (!selectedTask) return null;

  return <TaskDetail task={selectedTask} onClose={() => setSelectedTaskId(null)} />;
}

function AppContent() {
  const { error } = useDatabase();

  // Initialize app-level hooks
  useTheme();
  useMobile();
  useKeyboardShortcuts();

  // Sync React Router with app store
  useRouterSync();

  const { updateAvailable, isUpdating, installUpdate } = useTauriUpdater();

  const { spacesLoading, spaces } = useSpaces();

  // Show loading only if spaces are loading AND no cached data is available
  // This prevents a brief flash during Zustand persist rehydration
  if (spacesLoading && spaces.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
        <div className="flex flex-col items-center gap-5 animate-fade-in">
          <div className="relative">
            <div className="w-14 h-14 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20">
              <span className="text-lg font-bold text-white">Z</span>
            </div>
            <div className="absolute -right-1 -bottom-1 w-6 h-6 rounded-full bg-surface-primary flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-accent-violet animate-spin" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-text-primary">Caricamento dati...</p>
            <p className="text-xs text-text-quaternary mt-1">Preparazione del workspace</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
        <div className="text-center animate-fade-in max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-accent-rose-dim flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-accent-rose" />
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">Errore nel caricamento</h2>
          <p className="text-sm text-text-tertiary mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 rounded-lg bg-accent-violet text-white text-sm font-medium hover:bg-accent-violet-light transition-colors shadow-lg shadow-accent-violet/20"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {updateAvailable && (
        <div className="flex items-center justify-center gap-3 px-4 py-2 bg-accent-violet text-white text-sm">
          <Download className="w-4 h-4" />
          <span>Nuova versione {updateAvailable.version} disponibile</span>
          <button
            onClick={installUpdate}
            disabled={isUpdating}
            className="px-3 py-1 rounded bg-white/20 hover:bg-white/30 text-white text-xs font-medium transition-colors disabled:opacity-50"
          >
            {isUpdating ? 'Aggiornamento...' : 'Aggiorna'}
          </button>
        </div>
      )}
      <div className="flex-1 flex overflow-hidden min-w-0">
        <Routes>
          {/* Item routes - for single-level project paths */}
          <Route path="/projects/:path/documents/:itemId" element={<DocumentRoute />} />
          <Route path="/projects/:path/chats/:itemId" element={<ChatRoute />} />
          <Route path="/projects/:path/voice-channels/:itemId" element={<VoiceChannelRoute />} />
          <Route path="/projects/:path/spreadsheets/:itemId" element={<SpreadsheetRoute />} />
          <Route path="/projects/:path/presentations/:itemId" element={<PresentationRoute />} />
          {/* Task routes handled by wildcard to support nested paths */}

          {/* Deviation routes */}
          <Route path="/deviations" element={<DeviationsViewRoute />} />
          <Route path="/deviations/person/:assignee" element={<DeviationsViewRoute />} />
          <Route path="/deviations/task/:taskId" element={<DeviationsViewRoute />} />

          {/* Project routes with path - use wildcard to support nested paths like Marketing/Campaigns/Q1 */}
          <Route path="/projects/*" element={<ProjectViewRoute />} />

          {/* Global view routes without path */}
          <Route path="/inbox" element={<InboxViewRoute />} />
          <Route path="/report" element={<ReportViewRoute />} />
          <Route path="/team/calendar" element={<TeamCalendarViewRoute />} />
          <Route path="/team" element={<TeamViewRoute />} />
          <Route path="/timesheet" element={<TimesheetViewRoute />} />
          <Route path="/my-day" element={<Navigate to="/inbox" replace />} />
          <Route path="/projects" element={<ProjectsViewRoute />} />

          {/* Profile routes */}
          <Route path="/profile/me" element={<ProfilePage />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/projects" replace />} />
        </Routes>
        <GlobalTaskDetail />
      </div>
      <ToastContainer />
      <ErrorToastContainer />
      <DialogProvider />
    </Layout>
  );
}

// Component that renders the correct view based on URL params
function ProjectViewRoute() {
  const { viewMode } = parseUrl();

  switch (viewMode) {
    case 'list':
      return <ListViewRoute />;
    case 'board':
      return <BoardViewRoute />;
    case 'gantt':
      return <GanttViewRoute />;
    case 'inbox':
      return <InboxViewRoute />;
    case 'team':
      return <TeamViewRoute />;
    case 'timesheet':
      return <TimesheetViewRoute />;
    case 'projects':
      return <ProjectsViewRoute />;
    case 'deviations':
      return <DeviationsViewRoute />;
    case 'sprint':
      return <SprintViewRoute />;
    case 'calendar':
      return <CalendarViewRoute />;
    case 'my-day':
      return <InboxViewRoute />;
    default:
      return <ListViewRoute />;
  }
}

// Wrap the app with DatabaseProvider and BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <RemoteDatabaseBanner />
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
            <Route path="/p/:shareToken" element={<PublicProfilePage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <OrganizationProvider>
                    <ProfilesProvider>
                    <DatabaseProvider>
                      <ExtendedDataProvider>
                        <AppContent />
                      </ExtendedDataProvider>
                    </DatabaseProvider>
                    </ProfilesProvider>
                  </OrganizationProvider>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
